import JSZip from "jszip";
import type { FeatureCollection, Geometry } from "geojson";
import { ApiError, OVERLAY_CONTENT_TYPE } from "../api/client";
import { parseKmz } from "./kmzOverlay";

/** What the cap on the Worker is; the message below quotes it. */
export const MAX_OVERLAY_MB = 20;

const EXTENSION_RE = /\.(kmz|kml)$/i;

export function isOverlayFile(filename: string): boolean {
  return EXTENSION_RE.test(filename.trim());
}

/** `Barracks 5.kmz` → `Barracks 5`: the name the reviewer most likely wants. */
export function suggestedOverlayName(filename: string): string {
  return filename.trim().replace(/\.[^.]+$/, "").trim();
}

export interface PreparedOverlay {
  filename: string;
  suggestedName: string;
  /** Always a KMZ, whatever was picked: a bare KML is zipped here. */
  kmz: Blob;
  fc: FeatureCollection<Geometry | null>;
  featureCount: number;
}

function readBytes(blob: Blob): Promise<ArrayBuffer> {
  // FileReader rather than blob.arrayBuffer(): every browser we run in has
  // both, but jsdom (the test runtime) only has this one.
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result as ArrayBuffer);
    r.onerror = () => rej(r.error ?? new Error("read failed"));
    r.readAsArrayBuffer(blob);
  });
}

async function wrapKml(kmlBytes: ArrayBuffer): Promise<Blob> {
  const zip = new JSZip();
  zip.file("doc.kml", kmlBytes);
  const out = await zip.generateAsync({ type: "arraybuffer" });
  return new Blob([out], { type: OVERLAY_CONTENT_TYPE });
}

/**
 * Turn a picked or dropped file into what the upload needs: KMZ bytes and the
 * parsed features (so the layer can draw the moment the Worker says yes).
 * `null` means the file is not readable as KML or KMZ; nothing is uploaded.
 */
export async function prepareOverlayFile(file: File): Promise<PreparedOverlay | null> {
  let kmz: Blob;
  try {
    const bytes = await readBytes(file);
    kmz = /\.kml$/i.test(file.name)
      ? await wrapKml(bytes)
      : new Blob([bytes], { type: OVERLAY_CONTENT_TYPE });
  } catch {
    return null;
  }
  const fc = await parseKmz(await readBytes(kmz));
  if (fc === null || fc.features.length === 0) return null;
  return {
    filename: file.name,
    suggestedName: suggestedOverlayName(file.name),
    kmz,
    fc,
    featureCount: fc.features.length,
  };
}

/** Plain words for whatever the upload or removal threw. */
export function describeOverlayError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "id_collision": {
        const existing = (err.body as { existingName?: string } | undefined)?.existingName;
        return `That name clashes with '${existing ?? "another overlay"}'. ` +
          "Pick another name or remove that one first.";
      }
      case "too_large":
        return `That file is over ${MAX_OVERLAY_MB} MB.`;
      case "invalid_kmz":
        return "Couldn't read that file as KML or KMZ.";
      case "invalid_name":
        return "That name needs at least one letter or number, and at most 80 characters.";
      case "conflict":
        return "Someone else changed the overlays at the same time. Try again.";
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : String(err);
}
